import { useState, useEffect, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { trainingBlockApi, trainingProgramApi } from '../../api'
import { trainingProgramActivationApi } from '../../api/trainingProgramActivationApi'
import type { TrainingTagDto } from '../../api/trainingBlockApi'
import { BLOCK_TAG_CATEGORIES, flattenTrainingTags } from '../../api/trainingBlockApi'
import type { ApiError } from '../../api/client'
import type { TrainingBlock, TrainingProgram, TrainingProgramActivation } from '../../types/api'
import { activationMatchesTemplateProgram } from '../../types/api'
import { Button, StatusBadge, ExperimentalTextImportOption } from '../ui'
import { BlockIndexLabel } from './BlockIndexLabel'
import { useTranslation } from '../../i18n/useTranslation'
import { useTheme } from '../../hooks/useTheme'
import { useAuth } from '../../contexts/AuthContext'
import { entitlementsOf, isCoachedTrainee } from '../../utils/entitlements'
import SoloLibraryLocked from './SoloLibraryLocked'
import { TagCategoryFilter } from './TagCategoryFilter'
import {
  emptyTagCategoryFilterState,
  matchesTagCategoryFilters,
  type TagCategoryFilterState,
} from './tagCategoryFilterUtils'

function programFilterTags(
  program: TrainingProgram,
  blockMap: Record<number, TrainingBlock | undefined>,
): string[] {
  const tags = new Set<string>(program.tags ?? [])
  program.days.forEach(day => {
    ;(day.blockTags ?? blockMap[day.blockId]?.tags ?? []).forEach(tag => tags.add(tag))
  })
  return Array.from(tags)
}

export default function TrainingPrograms() {
  const navigate = useNavigate()
  const theme = useTheme()
  const { t } = useTranslation()
  const { user } = useAuth()
  const canCreate = entitlementsOf(user).createOwnBlocksAndPrograms
  const blocked = isCoachedTrainee(user)

  const [programs, setPrograms] = useState<TrainingProgram[]>([])
  const [blocks, setBlocks] = useState<TrainingBlock[]>([])
  const [availableTags, setAvailableTags] = useState<TrainingTagDto[]>([])
  const [activations, setActivations] = useState<TrainingProgramActivation[]>([])
  const [coachAssignedProgram, setCoachAssignedProgram] = useState<TrainingProgram | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeFilters, setActiveFilters] = useState<TagCategoryFilterState>(emptyTagCategoryFilterState())

  const blockMap = useMemo(
    () => Object.fromEntries(blocks.map(b => [b.id, b])),
    [blocks]
  )

  const tagEmojiMap = useMemo(
    () => Object.fromEntries(availableTags.map(tag => [tag.key, tag.emoji])),
    [availableTags]
  )

  const activeOwnActivation = useMemo(
    () => activations.find(a => a.isActive && !a.assignedByCoachId) ?? null,
    [activations]
  )

  const coachAssignedActivation = useMemo(
    () => activations.find(a => a.isActive && a.assignedByCoachId != null) ?? null,
    [activations]
  )

  const usedTagKeys = useMemo(() => {
    const keys = new Set<string>()
    programs.forEach(p => programFilterTags(p, blockMap).forEach(tag => keys.add(tag)))
    return keys
  }, [programs, blockMap])

  const filteredPrograms = useMemo(() => {
    return programs.filter(p => matchesTagCategoryFilters(programFilterTags(p, blockMap), activeFilters))
  }, [programs, activeFilters, blockMap])

  const loadData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [programsData, blocksData, tagsData, activationsData] = await Promise.all([
        trainingProgramApi.getPrograms(),
        trainingBlockApi.getBlocks(),
        trainingBlockApi.getTags(),
        trainingProgramActivationApi.getHistory(),
      ])
      setPrograms(programsData)
      setBlocks(blocksData)
      setAvailableTags(flattenTrainingTags(tagsData))
      setActivations(activationsData)

      const assigned = activationsData.find(a => a.isActive && a.assignedByCoachId != null)
      if (assigned) {
        const assignedProgramId = assigned.programId
        const alreadyLoaded = programsData.find(p => p.id === assignedProgramId)
        if (alreadyLoaded) {
          setCoachAssignedProgram(alreadyLoaded)
        } else {
          const ap = await trainingProgramApi.getProgram(assignedProgramId).catch(() => null)
          setCoachAssignedProgram(ap)
        }
      }
    } catch (err) {
      const apiError = err as ApiError
      setError(apiError.message || t.trainingPrograms.error.loadFailed)
    } finally {
      setLoading(false)
    }
  }, [t.trainingPrograms.error.loadFailed])

  useEffect(() => {
    if (blocked) return
    void loadData()
  }, [blocked, loadData])

  async function handleCopy(id: number, name: string) {
    if (!confirm(t.trainingPrograms.copyConfirm.replace('{{name}}', name))) return
    try {
      const source = await trainingProgramApi.getProgram(id)
      const copy = await trainingProgramApi.createProgram({
        name: `${t.trainingPrograms.copyPrefix} ${source.name}`,
        blockIds: [...source.days].sort((a, b) => a.sortOrder - b.sortOrder).map(d => d.blockId),
      })
      setPrograms(prev => [copy, ...prev])
    } catch (err) {
      const apiError = err as ApiError
      alert(apiError.message || t.trainingPrograms.copyFailed)
    }
  }

  async function handleDelete(id: number, name: string) {
    if (!confirm(t.trainingPrograms.error.deleteConfirm.replace('{{name}}', name))) return
    try {
      await trainingProgramApi.deleteProgram(id)
      setPrograms(prev => prev.filter(p => p.id !== id))
    } catch (err) {
      const apiError = err as ApiError
      alert(apiError.message || t.trainingPrograms.error.deleteFailed)
    }
  }

  if (blocked) {
    return <SoloLibraryLocked pageTitle={t.trainingPrograms.title} />
  }

  return (
    <div className="p-5">
      <header className="mb-6">
        <div className="flex items-start justify-between gap-2">
          <h1 className="text-2xl font-bold mb-1" style={{ color: theme.text.primary }}>
            {t.trainingPrograms.title}
          </h1>
          <button
            type="button"
            onClick={() => navigate('/history?tab=programs')}
            className="text-sm font-medium mt-1 shrink-0"
            style={{ color: theme.accent.text }}
          >
            {t.trainingPrograms.activationHistory}
          </button>
        </div>
        <p className="text-sm" style={{ color: theme.text.muted }}>{t.trainingPrograms.subtitle}</p>
      </header>

      {canCreate ? (
        <>
          <Button
            type="button"
            variant="primary"
            size="md"
            fullWidth
            className="mb-3"
            onClick={() => navigate('/training-programs/create')}
          >
            {t.trainingPrograms.createProgram}
          </Button>

          <div className="flex items-center gap-3 mb-3">
            <div className="flex-1 h-px" style={{ backgroundColor: theme.cardBorder }} />
            <span className="text-xs" style={{ color: theme.text.muted }}>{t.createTrainingBlock.orDivider}</span>
            <div className="flex-1 h-px" style={{ backgroundColor: theme.cardBorder }} />
          </div>

          <ExperimentalTextImportOption
            className="mb-6"
            title={t.trainingPrograms.createFromText}
            description={t.trainingPrograms.createFromTextHint}
            onClick={() => navigate('/training-programs/create-from-text')}
          />
        </>
      ) : (
        <p className="text-sm mb-6" style={{ color: theme.text.muted }}>
          {t.subscription.locked.createOwn}
        </p>
      )}

      {error && (
        <div className="rounded-xl p-4 mb-4 bg-red-500/10 border border-red-500/25">
          <p className="text-sm text-red-500">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="text-center py-10" style={{ color: theme.text.muted }}>{t.trainingPrograms.loading}</div>
      ) : (
        <>
          {/* Coach-assigned program — pinned at top */}
          {coachAssignedProgram && coachAssignedActivation && (
            <div className="mb-5">
              <div
                className="rounded-2xl overflow-hidden cursor-pointer transition-opacity hover:opacity-90 active:opacity-70"
                style={{
                  backgroundColor: theme.cardBg,
                  border: '2px solid rgba(0,136,204,0.35)',
                  boxShadow: '0 0 0 4px rgba(0,136,204,0.06)',
                }}
                onClick={() => navigate(`/training-programs/${coachAssignedProgram.id}`)}
              >
                <div className="flex items-center justify-between px-4 py-3.5">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold truncate" style={{ color: theme.text.primary }}>
                        {coachAssignedProgram.name}
                      </p>
                      <StatusBadge status={t.trainingPrograms.assignProgram.fromCoachBadge} variant="brand" size="sm" />
                      <StatusBadge status={t.trainingPrograms.activeBadge} variant="success" size="sm" />
                    </div>
                    <p className="text-xs mt-0.5" style={{ color: theme.text.muted }}>
                      {t.trainingPrograms.blockCount_other.replace('{{count}}', String(coachAssignedProgram.days.length))}
                    </p>
                  </div>
                </div>
                {coachAssignedProgram.days.length > 0 && (
                  <div style={{ borderTop: `1px solid ${theme.cardBorder}` }}>
                    {coachAssignedProgram.days.map((day, idx) => {
                      const tags = day.blockTags ?? blockMap[day.blockId]?.tags ?? []
                      const isLast = idx === coachAssignedProgram.days.length - 1
                      return (
                        <div
                          key={day.id}
                          className="flex items-start gap-3 px-4 py-2.5"
                          style={{ borderBottom: isLast ? undefined : `1px solid ${theme.cardBorder}` }}
                        >
                          <div className="flex-1 min-w-0">
                            <BlockIndexLabel index={day.sortOrder + 1} className="block mb-0.5" />
                            <p className="text-sm font-medium" style={{ color: theme.text.primary }}>
                              {day.blockName ?? `Block #${day.blockId}`}
                            </p>
                            {tags.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1">
                                {tags.slice(0, 3).map(tag => (
                                  <span
                                    key={tag}
                                    className="flex items-center gap-0.5 text-xs px-1.5 py-0.5 rounded-full"
                                    style={{
                                      backgroundColor: `${theme.buttonColor}12`,
                                      border: `1px solid ${theme.buttonColor}25`,
                                      color: theme.accent.text,
                                    }}
                                  >
                                    {tagEmojiMap[tag] && <span className="leading-none">{tagEmojiMap[tag]}</span>}
                                    <span>{t.trainingTags[tag as keyof typeof t.trainingTags] ?? tag}</span>
                                  </span>
                                ))}
                                {tags.length > 3 && (
                                  <span className="text-xs" style={{ color: theme.text.muted }}>
                                    +{tags.length - 3}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          <TagCategoryFilter
            className="mb-5"
            tags={availableTags}
            usedTagKeys={usedTagKeys}
            categories={BLOCK_TAG_CATEGORIES}
            value={activeFilters}
            onChange={setActiveFilters}
          />

          {/* Own programs */}
          {programs.length === 0 && !coachAssignedProgram ? (
            <div className="flex flex-col items-center py-14 text-center">
              <span className="text-5xl mb-4">📋</span>
              <p className="font-semibold mb-1" style={{ color: theme.text.primary }}>
                {t.trainingPrograms.emptyState.title}
              </p>
              <p className="text-sm px-8" style={{ color: theme.text.muted }}>
                {t.trainingPrograms.emptyState.description}
              </p>
            </div>
          ) : programs.length === 0 ? null : filteredPrograms.length === 0 ? (
            <div className="flex flex-col items-center py-14 text-center">
              <span className="text-5xl mb-4">🔍</span>
              <p className="font-semibold mb-1" style={{ color: theme.text.primary }}>
                {t.trainingPrograms.filterEmpty}
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {filteredPrograms.map(program => (
                <div
                  key={program.id}
                  className="rounded-2xl overflow-hidden cursor-pointer transition-opacity hover:opacity-90 active:opacity-70"
                  style={{ backgroundColor: theme.cardBg, border: `1px solid ${theme.cardBorder}` }}
                  onClick={() => navigate(`/training-programs/${program.id}`)}
                >
                  <div className="px-4 py-3.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold truncate" style={{ color: theme.text.primary }}>
                        {program.name}
                      </p>
                      {activeOwnActivation && activationMatchesTemplateProgram(activeOwnActivation, program.id) && (
                        <StatusBadge status={t.trainingPrograms.activeBadge} variant="success" size="sm" />
                      )}
                    </div>
                    {program.tags && program.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-1.5">
                        {program.tags.map(tag => (
                          <span
                            key={tag}
                            className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full"
                            style={{ backgroundColor: `${theme.buttonColor}18`, color: theme.text.secondary }}
                          >
                            <span>{tagEmojiMap[tag] ?? ''}</span>
                            <span>{t.trainingTags[tag as keyof typeof t.trainingTags] ?? tag}</span>
                          </span>
                        ))}
                      </div>
                    )}
                    <p className="text-xs mt-0.5" style={{ color: theme.text.muted }}>
                      {t.trainingPrograms.blockCount_other.replace('{{count}}', String(program.days.length))}
                    </p>
                  </div>

                  {program.days.length > 0 && (
                    <div style={{ borderTop: `1px solid ${theme.cardBorder}` }}>
                      {program.days.map((day, idx) => {
                        const block = blockMap[day.blockId]
                        const isLast = idx === program.days.length - 1
                        return (
                          <div
                            key={day.id}
                            className="flex items-start gap-3 px-4 py-2.5"
                            style={{ borderBottom: isLast ? undefined : `1px solid ${theme.cardBorder}` }}
                          >
                            <div className="flex-1 min-w-0">
                              <BlockIndexLabel index={day.sortOrder + 1} className="block mb-0.5" />
                              <p className="text-sm font-medium" style={{ color: theme.text.primary }}>
                                {day.blockName ?? `Block #${day.blockId}`}
                              </p>
                              {block?.tags?.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {block.tags.slice(0, 3).map(tag => (
                                    <span
                                      key={tag}
                                      className="flex items-center gap-0.5 text-xs px-1.5 py-0.5 rounded-full"
                                      style={{
                                        backgroundColor: `${theme.buttonColor}12`,
                                        border: `1px solid ${theme.buttonColor}25`,
                                        color: theme.accent.text,
                                      }}
                                    >
                                      {tagEmojiMap[tag] && <span className="leading-none">{tagEmojiMap[tag]}</span>}
                                      <span>{t.trainingTags[tag as keyof typeof t.trainingTags] ?? tag}</span>
                                    </span>
                                  ))}
                                  {block.tags.length > 3 && (
                                    <span className="text-xs" style={{ color: theme.text.muted }}>
                                      +{block.tags.length - 3}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}

                  <div
                    className="flex items-center justify-between px-3 py-2"
                    style={{ borderTop: `1px solid ${theme.cardBorder}` }}
                    onClick={e => e.stopPropagation()}
                  >
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        title={t.trainingPrograms.editProgram}
                        onClick={() => navigate(`/training-programs/${program.id}/edit`)}
                        className="p-3 rounded-xl transition-colors"
                        style={{ color: theme.accent.text }}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        title={t.trainingPrograms.copyProgram}
                        onClick={() => handleCopy(program.id, program.name)}
                        className="p-3 rounded-xl transition-opacity"
                        style={{ color: theme.text.muted }}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                        </svg>
                      </button>
                    </div>
                    <button
                      type="button"
                      title={t.trainingPrograms.deleteProgram}
                      onClick={() => handleDelete(program.id, program.name)}
                      className="p-3 rounded-xl transition-colors"
                      style={{ color: '#ef4444' }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                        <path d="M10 11v6" /><path d="M14 11v6" />
                        <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}
